import useFormatCurrencyForItinerary from '@/src/hooks/currency/useFormatCurrencyForItinerary'
import { VasAncillary } from '@/src/types/itinerary'
import { KeyboardArrowDown } from '@mui/icons-material'
import { Box, Button, Card, Divider, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { benefitsIcons } from '.'
import BenefitsModal from './BenefitsModal'
interface InsuranceCardProps {
    insurance: VasAncillary
    isSelected: boolean
    onInsuranceClick: () => void
    index: number
}

const InsuranceCard = ({ insurance, isSelected, onInsuranceClick, index }: InsuranceCardProps) => {
    const tCommon = useTranslations('common')
    const locale = useLocale()
    const { formatCurrency } = useFormatCurrencyForItinerary()
    const [showAllBenefits, setShowAllBenefits] = useState(false)
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))

    const vasDetail = insurance.vasDetails?.[0]
    const {
        text: title = '',
        icon: imageSrc = '',
        shortDescription = '',
        description,
        tripPremiumDetails: { totalPremiumAmount = 0 },
        benefits = [],
        termsAndConditions: { url: termsAndConditionsUrl = '' }
    } = vasDetail || {}

    // Show 3 benefits
    const renderNumberOfBenefitsInCard = benefits.length > 3 ? 3 : benefits.length

    const isRemainingBenefitsVisible = benefits.length > 3

    const handleViewAllBenefits = (e: React.MouseEvent) => {
        e.stopPropagation()
        setShowAllBenefits(true)
    }

    const handleCloseModal = () => {
        setShowAllBenefits(false)
    }

    return (
        <>
            <Card id={`${insurance.vasType}_${index}`} onClick={onInsuranceClick} sx={{ cursor: 'pointer' }}>
                {/* Header */}
                <Box
                    sx={{
                        bgcolor: '#90b4e833',
                        p: { xs: 1, md: 1 },
                        px: { xs: 1.5, md: 2 },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        gap: { xs: 1, md: 0 }
                    }}
                >
                    <Box display={'flex'} alignItems={'center'} gap={1}>
                        {/* <TravelPlusLogo /> */}
                        <Image
                            src={
                                locale === 'ar'
                                    ? 'https://ui.cltp.co/assets/travelplusAR.png'
                                    : 'https://ui.cltp.co/assets/travelplusEng.png'
                            }
                            alt={'travel plus logo'}
                            width={isMobile ? 80 : 120}
                            height={isMobile ? 22 : 28}
                        />
                    </Box>

                    <Box display={'flex'} alignItems={'center'} gap={1}>
                        <Typography fontWeight={600} sx={{ fontSize: { xs: '14px', md: '16px' } }}>
                            {formatCurrency(totalPremiumAmount)}
                        </Typography>
                        <Button variant={isSelected ? 'contained' : 'outlined'} sx={{ width: '80px' }} size="small">
                            {isSelected ? tCommon('remove') : tCommon('add')}
                        </Button>
                    </Box>
                </Box>

                {/* Benefits Section */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', md: 'center' },
                        p: { xs: 1.5, md: 2 },
                        gap: { xs: 0.5, md: 1.5 },
                        flexDirection: { xs: 'column', md: 'row' },
                        flexWrap: { xs: 'wrap', md: 'nowrap' }
                    }}
                >
                    {benefits?.slice(0, renderNumberOfBenefitsInCard).map((benefit, benefitIndex) => {
                        const titleStr = benefit.title.split('||')
                        const title = titleStr[1]
                        const description = titleStr[0]
                        const icon = benefitsIcons[benefitIndex]
                        return (
                            <Box
                                key={benefitIndex}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: { xs: '45%', md: 'auto' },
                                    mb: { xs: 1, md: 0 }
                                }}
                            >
                                <Image
                                    src={icon}
                                    alt={benefit.title}
                                    width={isMobile ? 24 : 30}
                                    height={isMobile ? 24 : 30}
                                />
                                <Box>
                                    <Typography
                                        variant="body1"
                                        fontWeight={600}
                                        sx={{
                                            fontSize: '12px',
                                            display: 'block'
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        fontWeight={500}
                                        color="textSecondary"
                                        sx={{
                                            fontSize: '11px',
                                            display: 'block'
                                        }}
                                    >
                                        {description}
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    })}
                    {isRemainingBenefitsVisible && (
                        <Button
                            variant="text"
                            size={isMobile ? 'small' : 'medium'}
                            disableRipple
                            endIcon={<KeyboardArrowDown fontSize="small" color="primary" />}
                            onClick={handleViewAllBenefits}
                            sx={{
                                minWidth: 'auto',
                                p: { xs: 0.5, md: 1 }
                            }}
                        >
                            <Typography color="primary" fontWeight={500} sx={{ fontSize: { xs: '12px', md: '13px' } }}>
                                {tCommon('viewAllBenefits')}
                            </Typography>
                        </Button>
                    )}
                </Box>

                {/* Divider */}
                <Divider
                    sx={{
                        borderStyle: 'dashed',
                        borderColor: 'borderColors.primary',
                        borderWidth: '1px',
                        borderTop: 'none'
                    }}
                />

                {/* Footer */}
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'space-between'
                    }}
                >
                    {/* <Image
                        src="http://tripmoneycmsimgak.mmtcdn.com/img/tawuniya_logo_03155034c2.png"
                        alt="tawuniya Logo"
                        width={isMobile ? 56 : 70}
                        height={isMobile ? 26 : 30}
                    /> */}
                    <Typography
                        variant="caption"
                        fontWeight={500}
                        color="textSecondary"
                        sx={{
                            fontSize: '11px',
                            lineHeight: { xs: 1.3, md: 1.4 },
                            flex: 2
                        }}
                    >
                        {tCommon('byAddingTravelInsurance')}{' '}
                        <Link
                            href={'https://ui.cltp.co/assets/travelInsurance.pdf'}
                            target="_blank"
                            style={{ textDecoration: 'none', color: '#2a5298' }}
                            onClick={e => {
                                e.stopPropagation()
                            }}
                        >
                            {tCommon('termsAndConditions')}
                        </Link>
                    </Typography>

                    <Box
                        display={'flex'}
                        alignItems={'center'}
                        gap={1}
                        flexWrap={isMobile ? 'wrap' : 'nowrap'}
                        flex={1}
                        justifyContent={'flex-end'}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            color="grey.600"
                            sx={{
                                fontSize: '11px',
                                lineHeight: { xs: '18px', md: '20px' }
                            }}
                        >
                            {tCommon('poweredBy')}
                        </Typography>

                        <Image
                            src={'https://ui.cltp.co/assets/tawuniya_transparent.png'}
                            alt="tawuniya Logo"
                            width={isMobile ? 60 : 80}
                            height={isMobile ? 20 : 30}
                        />
                    </Box>
                </Box>
            </Card>

            {/* Benefits Modal */}
            <BenefitsModal
                isOpen={showAllBenefits}
                onClose={handleCloseModal}
                insurance={insurance}
                isSelected={isSelected}
                onInsuranceClick={onInsuranceClick}
            />
        </>
    )
}

export default InsuranceCard
